import * as React from 'react';
import SurveyHeader from '../project/survey/SurveyHeader';
import { useMsgText } from '../localization/hooks';
import { Str } from '../localization/translationKeys';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import { css } from '@emotion/core';
import LocalBtn from '../localization/LocalBtn';
import { btnStyle } from '../forms/styles/button';
import { COLORS } from '../globalStyle/colors';
import Msg from '../localization/Msg';
import { useAppState } from '../core/redux/hooks';
import { useHistory } from 'react-router-dom';
import { useRoutes } from '../navigation/routes';
import LocalDiv from '../localization/LocalDiv';
import { useProjIds, useRaterApprovalIds } from '../navigation/hooks';
import PortalAutoLayout from '../layout/PortalAutoLayout';
import { PortalType } from '../navigation/portalType';


const styles = {
  content: css`
    margin-top: 57px;
    ${onMobileDevice} {
      margin-top: 20px;
    }
  `,
  header: css`
    ${onMobileDevice} {
      margin-top: 37px;
    }  
  `,
  labels:  {
    title: css`
      font-weight: bold;
    `,
    value: css`
      margin-bottom: 20px;
    `,
  },
  buttons: {
    button: css`
      min-width: 250px;
      margin-top: 10px;
    `,
  },
};

type Props = {
  className?: string;
};


/**
 * Account: a component that shows current account information
 *
 * PureComponent: true
 * Redux Connected: false
 */
const Account: React.FC<Props> = props => {
  const history = useHistory();

  const pageTitle = useMsgText(Str.Account);

  const user = useAppState(state => state.auth.currentUser);
  const ssoInfo  = useAppState(state => state.auth.ssoRedirect);

  const isSSO = !!ssoInfo && ssoInfo.isSso;
  
  const lowerCasePath = history.location.pathname.toLowerCase();
  const raterApproval = lowerCasePath.indexOf(PortalType.RATER_APPROVAL.toLowerCase()) > -1;
  const reportRecipient = lowerCasePath.indexOf(PortalType.REPORT_RECIPIENT.toLowerCase()) > -1;

  const projIds = useProjIds();
  const routeIds = useRaterApprovalIds();

  const projectId = raterApproval ? routeIds.projDisplayId : projIds.projDisplayId;
  const participantId = raterApproval ? routeIds.participantId : projIds.participantId;

  const routes = useRoutes(participantId, projectId);

  const passwordClicked = React.useCallback(
    () =>
      history.push(
        raterApproval
        ? routes.RaterApprovalChangePassword
        : reportRecipient
          ? routes.ReportRecipientChangePassword
          : routes.ChangePassword,
      ),
      [
        history,
        raterApproval,
        reportRecipient,
        routes.ChangePassword,
        routes.RaterApprovalChangePassword,
        routes.ReportRecipientChangePassword,
      ],
  );

  const questionClicked = React.useCallback(
    () =>
      history.push(
        raterApproval
        ? routes.RaterApprovalChangeSecurityQuestion
        : reportRecipient
          ? routes.ReportRecipientChangeSecurityQuestion
          : routes.ChangeSecurityQuestion,
      ),
    [
      history,
      raterApproval,
      reportRecipient,
      routes.ChangeSecurityQuestion,
      routes.RaterApprovalChangeSecurityQuestion,
      routes.ReportRecipientChangeSecurityQuestion,
    ],
  );

  return (
    <PortalAutoLayout className={props.className}>
      <SurveyHeader title={`/ ${pageTitle}`} deadline={undefined} css={styles.header} />
      <LocalDiv css={styles.content}>
        <Msg str={Str.Name} css={styles.labels.title} />
        <div css={styles.labels.value}>
          {user?.personName}
        </div>
        <Msg str={Str.Company} css={styles.labels.title} />
        <div css={styles.labels.value}>
          {user?.clientName}
        </div>
        <Msg str={Str.Email} css={styles.labels.title} />
        <div css={styles.labels.value}>
          {user?.userName}
        </div>

        {
          !isSSO && (
            <div>
              <LocalBtn
                str={Str.ChangePassword}
                css={[btnStyle.primary(COLORS.BLUE), styles.buttons.button]}
                onClick={() => passwordClicked()}
              />
            </div>
          )
        }
        {
          !isSSO && (
            <div>
              <LocalBtn
                str={Str.ChangeSecurityQuestion}
                css={[btnStyle.primary(COLORS.BLUE), styles.buttons.button]}
                onClick={() => questionClicked()}
              />
            </div>
          )
        }        
      </LocalDiv>
    </PortalAutoLayout>
  );
};

export default Account;
